// Topbar day clock — check in, break, check out — and, beside it, the project
// timer (owner, 2026-10-14): Start timer, the running timer, Switch and Stop.
//
// The day clock is deliberately a thin mirror of the DayClockCard on the
// Timesheets screen: both read the day through `loadDay` + `summarize` and
// mutate through the same `dayclock` helpers, so they always agree and either
// one can drive the day. The shared CheckoutModal lives here and is imported by
// the screen. A project timer is a segment of that day (lib/projectTimer): the
// server checks you in when one starts, and a break or a check-out ends it.
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useOrgDates } from '../lib/orgSettings';
import { useAuth } from '../lib/auth';
import { Badge, Button, Icon, Mono, TONE, markColor } from './primitives';
import { Dialog } from './dialog';
import { Field, TextArea, TextInput } from './field';
import { InlineStatus, LoadError, useToast } from './feedback';
import { fmtDayKey, orgDateKey } from '../lib/dates';
import { fmtDuration } from '../lib/duration';
import {
  checkIn,
  checkOut,
  loadDay,
  loadStaleSession,
  onApprovedLeaveToday,
  resumeClock,
  summarize,
  takeBreak,
  type ClockEntry,
  type ClockResult,
} from '../lib/dayclock';
import {
  NOTE_MAX,
  canTimeProjects,
  loadTimerTasks,
  createTimerTask,
  loadRunningTimer,
  loadTimeable,
  setRunningTimer,
  startTimer,
  stopTimer,
  useRunningTimer,
  type TimerTask,
  type TimeableProject,
} from '../lib/projectTimer';

// ── Checkout prompt ───────────────────────────────────────────────────────────
// Optional day-summary prompt shown on check-out. Blank = plain checkout; the
// summary is never required.
//
// On the shared Dialog (DS-07, TS-01, SH-03, XA-06). It began as a hard-coded
// near-black box that Escape and the backdrop ignored, with the hours worked and
// Cancel dark on dark in light theme; Phase 0 gave it tokens, Escape and focus
// return by hand. The Dialog owns all of that now, plus the focus trap it never
// had: focus starts in the summary, a typed summary is asked about before a
// stray click or Escape discards it, and closing returns focus to whichever
// Check out opened it. The confirm is primary, not rose: checking out is the
// routine end of a day, and rose is reserved for a state that is wrong.
export function CheckoutModal({
  seconds,
  onConfirm,
  onCancel,
}: {
  seconds: number;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}) {
  const [summary, setSummary] = useState('');
  return (
    <Dialog
      open
      size="sm"
      title="Wrap up your day"
      onClose={onCancel}
      dirty={summary.trim() !== ''}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onConfirm(summary)}>
            Check out
          </Button>
        </>
      )}
    >
      {/* The number this dialog exists to confirm — a figure, not a muted aside. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--hf-space-2)',
          marginBottom: 'var(--hf-space-4)',
        }}
      >
        <Mono style={{ fontSize: 'var(--hf-text-2xl)', color: 'var(--hf-ink)' }}>
          {fmtDuration(seconds, 'clock')}
        </Mono>
        <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>
          worked today
        </span>
      </div>
      <Field label="Day summary" hint="Optional. Leave it blank to just check out.">
        <TextArea
          rows={4}
          value={summary}
          onChange={setSummary}
          placeholder="e.g. Finished the storyboard, two rounds of client notes"
        />
      </Field>
    </Dialog>
  );
}

// The two round buttons inside the running pill. Button, so they are named,
// focus-ringed and hover from the stylesheet; they used to repaint themselves
// from onMouseEnter, which no keyboard ever triggered.
const ROUND: CSSProperties = {
  width: 28,
  height: 28,
  padding: 0,
  justifyContent: 'center',
  flexShrink: 0,
  borderRadius: 'var(--hf-radius-pill)',
};

// ── Day clock pill (topbar) ───────────────────────────────────────────────────
// `dayClock={false}` on Timesheets, whose DayClockCard is the day's control:
// only the project timer shows there.
export function SmartTimerPill({ dayClock = true }: { dayClock?: boolean }) {
  const { session, perms } = useAuth();
  const uid = session?.user.id;
  const toast = useToast();
  const [day, setDay] = useState<ClockEntry[]>([]);
  // The read failed. Not the same as "not checked in": offering Check in over
  // a day that could not be read invites a second session (TS-14).
  const [failed, setFailed] = useState(false);
  // A session left open on an earlier org day. Today's read never shows it, and
  // one_active_time_entry refuses every check-in until it is closed.
  const [stale, setStale] = useState<ClockEntry | null>(null);
  const [now, setNow] = useState(Date.now());
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Today is approved leave — warn, never block. The server settles the day.
  const [leaveToday, setLeaveToday] = useState(false);
  const busyRef = useRef(false);

  const { tz } = useOrgDates();
  const refresh = useCallback(async () => {
    if (!uid) return { rows: null, stale: null };
    const [rows, staleRow, onLeave] = await Promise.all([
      loadDay(uid, tz),
      loadStaleSession(uid, tz),
      onApprovedLeaveToday(uid, tz),
    ]);
    // A failed stale read fails the pill too: the Check in it would otherwise
    // offer may be the one the server is bound to refuse (UI-CONVENTIONS §7).
    setFailed(rows === null || staleRow === undefined);
    if (rows) setDay(rows);
    setStale(staleRow ?? null);
    setLeaveToday(onLeave);
    return { rows, stale: staleRow };
  }, [uid, tz]);

  // A clock action that did not land. The pill has no room for a sentence, so it
  // is a page toast — an error one, which stays until dismissed.
  const report = (
    what: string,
    r: Exclude<ClockResult, { ok: true }>,
    rows: ClockEntry[],
    stale: ClockEntry | null | undefined,
  ) => {
    if (r.reason !== 'open_session') {
      toast.push({ tone: 'error', message: `Couldn’t ${what}. ${r.message}` });
      return;
    }
    // Today's session, opened in another tab: the pill now shows it, and that
    // is the whole answer.
    if (rows.some((e) => e.status !== 'logged')) return;
    // Otherwise it is one left open on an earlier day, which today's read never
    // shows; the re-read found it, and the pill now links to it. This used to be
    // a button that did nothing, every time.
    toast.push({
      tone: 'error',
      message: stale
        ? `Couldn’t check in: your session from ${fmtDayKey(orgDateKey(stale.created_at, tz), { weekday: true })} is still open. Timesheets shows how it gets closed.`
        : `Couldn’t check in. ${r.message}`,
    });
  };

  // Serialize clock mutations so rapid clicks / double-taps can't race.
  const run = async (what: string, fn: () => Promise<ClockResult>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const r = await fn();
      const { rows, stale } = await refresh();
      if (!r.ok) {
        // Nothing was unmounted, so the opener still has focus, unless the re-read
        // found an earlier day's session: its link took the Check in's place.
        refocus.current = stale != null;
        report(what, r, rows ?? [], stale);
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
      // Same-tab sync: nudge the Timesheets day-clock card (and any other
      // listener) to re-read, so a check-in/out here reflects there without
      // waiting on the Realtime round-trip.
      window.dispatchEvent(new Event('hf:timesync'));
    }
  };

  // A session that crossed the org's midnight with the pill up is an earlier
  // day's now, whose check-out the server caps (20261009230000) where the wrap-up
  // would say "worked today". The re-read makes the pill the link to Timesheets,
  // whose confirm says what lands.
  const onCheckOut = (e: ClockEntry) => {
    if (orgDateKey(e.created_at, tz) < orgDateKey(Date.now(), tz)) {
      refocus.current = true;
      void refresh();
    } else setCheckoutOpen(true);
  };

  useEffect(() => {
    void refresh();
    if (!supabase || !uid) return;
    const sb = supabase;
    const ch = sb
      .channel(`timer:${uid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'time_entries', filter: `user_id=eq.${uid}` },
        () => void refresh(),
      )
      .subscribe();
    // Same-tab sync: a clock action on the Timesheets screen dispatches
    // hf:timesync, so the pill re-reads immediately.
    const onSync = () => void refresh();
    window.addEventListener('hf:timesync', onSync);
    return () => {
      void sb.removeChannel(ch);
      window.removeEventListener('hf:timesync', onSync);
    };
  }, [uid, refresh]);

  // Same derived view the Timesheets card uses — one source of truth for state.
  const { active, totalSeconds, state } = summarize(day, now);

  // 1s tick only while running
  useEffect(() => {
    if (state !== 'in') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [state]);

  // A confirmed check-out swaps the pill for the Check in again button, so the
  // Check out that opened the dialog is gone and the focus it was handed back
  // fell to <body>. It goes to the button that took the pill's place, unless the
  // person has already moved somewhere else. Or to the Still checked in link: it
  // takes a refused Check in's place with the state still 'none', hence `stale`.
  // It goes to the wrapper, never the button: a focused Check in again took the
  // Space that woke a sleeping PC as a click (bug report, 2026-10-09). One Tab
  // reaches the button. A browser that can't focus a display: contents box leaves
  // focus on <body>, which is safe.
  const refocus = useRef(false);
  const checkInRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    if (document.activeElement === document.body) checkInRef.current?.focus();
  }, [state, stale]);

  if (!supabase || !uid) return null;

  // Not over an earlier day's open session: start_timer refuses it as Check in
  // does, and the link below is the way out of both.
  const timer =
    canTimeProjects(perms) && !stale ? (
      <ProjectTimer
        uid={uid}
        checkedOut={!active}
        onBreak={state === 'break'}
        onLeave={leaveToday && !active}
      />
    ) : null;
  if (!dayClock) return failed ? null : timer;

  if (failed) {
    return (
      <Button variant="ghost" size="sm" icon="clock" onClick={() => void refresh()}>
        Day clock unavailable · Retry
      </Button>
    );
  }

  // Still open from an earlier day, so a Check in here would only be refused.
  // Timesheets checks it out and says what that records; going there is
  // navigation, so a link (UI-CONVENTIONS), in the Check in's wrapper and
  // Button's sm box, so the phone's clock bar sizes it the same (tokens.css).
  if (!active && stale) {
    return (
      <span ref={checkInRef} tabIndex={-1} style={{ display: 'contents' }}>
        <Link
          to="/timesheets"
          className="hf-btn"
          data-variant="secondary"
          title="Check out of it on Timesheets, and today starts fresh"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            fontSize: 12,
            fontWeight: 500,
            fontFamily: 'var(--hf-font-ui)',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            borderRadius: 'var(--hf-radius-pill)',
            background: TONE.warning.bg,
            borderColor: TONE.warning.bd,
            color: TONE.warning.fg,
          }}
        >
          <Icon name="clock" size={14} />
          Still checked in · {fmtDayKey(orgDateKey(stale.created_at, tz), { weekday: true })}
        </Link>
      </span>
    );
  }

  // Not on the clock — one click starts the day (or restarts it after checkout).
  if (!active) {
    return (
      <span ref={checkInRef} tabIndex={-1} style={{ display: 'contents' }}>
        <Button
          variant="secondary"
          size="sm"
          icon="play"
          loading={busy}
          onClick={() => void run('check in', () => checkIn(uid))}
          title={
            leaveToday
              ? "You're on approved leave today — checking in will give the leave day back (in part or in full) when you check out"
              : 'Check in for the day'
          }
          style={{
            borderRadius: 'var(--hf-radius-pill)',
            ...(leaveToday
              ? {
                  background: TONE.warning.bg,
                  borderColor: TONE.warning.bd,
                  color: TONE.warning.fg,
                }
              : null),
          }}
        >
          {state === 'out' ? 'Check in again' : 'Check in'}
          {/* Say it on the face of the button, not only in the tooltip. */}
          {leaveToday && <span style={{ fontSize: 'var(--hf-text-2xs)' }}>· on leave</span>}
        </Button>
        {timer}
      </span>
    );
  }

  const running = state === 'in';
  // -text tokens for the words: --hf-accent as 11px text is 3.1:1 on dark, and
  // raw amber is 1.6:1 in light (TS-05, XA-18). The dot is a fill.
  const tone = running ? TONE.accent : TONE.warning;

  return (
    <>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--hf-space-1)',
          padding: 'var(--hf-space-1) var(--hf-space-1) var(--hf-space-1) var(--hf-space-3)',
          borderRadius: 'var(--hf-radius-pill)',
          background: tone.bg,
          border: `1px solid ${tone.bd}`,
          boxShadow: running ? '0 0 20px -4px var(--hf-accent-glow)' : 'none',
          transition:
            'background-color var(--hf-dur-state) var(--hf-ease), border-color var(--hf-dur-state) var(--hf-ease)',
        }}
      >
        <span
          className={running ? 'hf-pulse-dot' : ''}
          style={{
            width: 6,
            height: 6,
            borderRadius: 'var(--hf-radius-pill)',
            background: markColor(running ? 'accent' : 'warning'),
          }}
        />
        {/* The earlier pill (owner, 2026-10-09: "keep the old timer style with
            accent colours"): the label in ink at reading size, the time in the
            accent, and the two actions as tinted rounds. */}
        <span
          style={{
            fontSize: 'var(--hf-text-sm)',
            color: 'var(--hf-ink)',
            fontWeight: 'var(--hf-weight-medium)',
            marginLeft: 'var(--hf-space-1)',
          }}
        >
          {running ? 'On the clock' : 'On break'}
        </span>
        {/* Day total, not just this session — matches the Timesheets card, and
            is the same H:MM:SS it shows (it was 00:00:03 here beside 0:03). */}
        <Mono
          style={{
            fontSize: 'var(--hf-text-sm)',
            color: tone.fg,
            fontWeight: 'var(--hf-weight-medium)',
            margin: '0 var(--hf-space-2)',
            minWidth: '8ch',
          }}
        >
          {fmtDuration(totalSeconds, 'clock')}
        </Mono>

        <Button
          variant="ghost"
          icon={running ? 'pause' : 'play'}
          ariaLabel={running ? 'Take a break' : 'Resume'}
          title={running ? 'Take a break' : 'Resume'}
          disabled={busy}
          focusableWhenDisabled
          onClick={() =>
            void run(running ? 'take a break' : 'resume', () =>
              running ? takeBreak(active) : resumeClock(active),
            )
          }
          style={{
            ...ROUND,
            color: running ? TONE.sage.fg : tone.fg,
            background: running ? TONE.sage.bg : tone.bg,
          }}
        />

        {/* Check out, in rose as the earlier pill drew it (owner's call,
            2026-10-09, over UI-CONVENTIONS §3's "rose is for wrong"). */}
        <Button
          variant="ghost"
          icon="stop"
          ariaLabel="Check out"
          title="Check out"
          disabled={busy}
          focusableWhenDisabled
          onClick={() => onCheckOut(active)}
          style={{ ...ROUND, color: TONE.rose.fg, background: TONE.rose.bg }}
        />
      </div>
      {timer}
      {checkoutOpen && (
        <CheckoutModal
          seconds={totalSeconds}
          onConfirm={(note) => {
            setCheckoutOpen(false);
            void run('check out', () => {
              refocus.current = true;
              return checkOut(active, note);
            });
          }}
          onCancel={() => setCheckoutOpen(false)}
        />
      )}
    </>
  );
}

// ── Project timer ─────────────────────────────────────────────────────────────
// Start timer when none runs; the running timer (project, issue, time) with
// Switch and Stop when one does. Stays live on project_time's realtime: a break
// or a check-out ends the segment on the server, and this hears it.
function ProjectTimer({
  uid,
  checkedOut,
  onBreak,
  onLeave,
}: {
  uid: string;
  /** Starting would check you in: the picker says so. */
  checkedOut: boolean;
  /** Starting ends the break, as Resume does. */
  onBreak: boolean;
  /** Approved leave today and checked out: warn, as Check in does. */
  onLeave: boolean;
}) {
  const toast = useToast();
  const timer = useRunningTimer();
  const [failed, setFailed] = useState(false);
  const [picking, setPicking] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [now, setNow] = useState(Date.now());

  const refresh = useCallback(async () => {
    const t = await loadRunningTimer(uid);
    setFailed(t === undefined);
    if (t !== undefined) setRunningTimer(t);
  }, [uid]);

  useEffect(() => {
    void refresh();
    if (!supabase) return;
    const sb = supabase;
    const ch = sb
      .channel(`project-timer:${uid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'project_time', filter: `user_id=eq.${uid}` },
        () => void refresh(),
      )
      .subscribe();
    // A check-out or break from the day clock (here or on Timesheets) ends the
    // segment server-side; re-read without waiting on the realtime round-trip.
    const onSync = () => void refresh();
    window.addEventListener('hf:timesync', onSync);
    return () => {
      void sb.removeChannel(ch);
      window.removeEventListener('hf:timesync', onSync);
    };
  }, [uid, refresh]);

  useEffect(() => {
    if (!timer) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [timer]);

  // One timer action, from its dialog. Done, or refused over a state the
  // re-read now shows (an earlier day's open session, a timer that had already
  // stopped): the dialog closes, the second with a toast. Any other refusal
  // stays in the dialog as its message: everything outside an open dialog is
  // inert, the toast host included.
  const act = async (
    what: string,
    fn: () => Promise<ClockResult>,
    close: () => void,
  ): Promise<string | null> => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    try {
      const r = await fn();
      await refresh();
      if (r.ok || r.reason !== 'error') close();
      if (r.ok) return null;
      if (r.reason === 'error') return r.message;
      toast.push(
        r.reason === 'open_session'
          ? {
              tone: 'error',
              message: `Couldn’t ${what}: ${r.message}. Timesheets shows how it gets closed.`,
            }
          : { tone: 'info', message: r.message },
      );
      return null;
    } finally {
      busyRef.current = false;
      setBusy(false);
      // A start may have checked you in or ended a break: the day clock re-reads.
      window.dispatchEvent(new Event('hf:timesync'));
    }
  };

  const picker = picking && (
    <TimerPicker
      uid={uid}
      switching={!!timer}
      currentProject={timer?.project_id ?? null}
      checkedOut={checkedOut}
      onBreak={onBreak}
      onLeave={onLeave}
      busy={busy}
      onClose={() => setPicking(false)}
      onStart={(project, task) =>
        act(
          'start the timer',
          () => startTimer(project, task),
          () => setPicking(false),
        )
      }
    />
  );

  if (failed)
    return (
      <Button variant="ghost" size="sm" icon="clock" onClick={() => void refresh()}>
        Timer unavailable · Retry
      </Button>
    );

  if (!timer)
    return (
      <>
        <Button
          variant="secondary"
          size="sm"
          icon="play"
          loading={busy}
          ariaHasPopup="dialog"
          onClick={() => setPicking(true)}
          title={
            onLeave
              ? "You're on approved leave today — starting a timer checks you in, and checking out gives the leave day back (in part or in full)"
              : 'Time your work on a project'
          }
          style={{
            borderRadius: 'var(--hf-radius-pill)',
            ...(onLeave
              ? {
                  background: TONE.warning.bg,
                  borderColor: TONE.warning.bd,
                  color: TONE.warning.fg,
                }
              : null),
          }}
        >
          Start timer
          {onLeave && <span style={{ fontSize: 'var(--hf-text-2xs)' }}>· on leave</span>}
        </Button>
        {picker}
      </>
    );

  const elapsed = Math.max(0, Math.floor((now - new Date(timer.started_at).getTime()) / 1000));
  const name = timer.task?.title ?? timer.project?.name ?? 'Project';
  const code = timer.task?.code ?? timer.project?.code;
  return (
    <>
      <div
        role="group"
        aria-label="Project timer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--hf-space-1)',
          minWidth: 0,
          padding: 'var(--hf-space-1) var(--hf-space-1) var(--hf-space-1) var(--hf-space-3)',
          borderRadius: 'var(--hf-radius-pill)',
          background: TONE.accent.bg,
          border: `1px solid ${TONE.accent.bd}`,
        }}
      >
        <span
          className="hf-pulse-dot"
          style={{
            width: 6,
            height: 6,
            flexShrink: 0,
            borderRadius: 'var(--hf-radius-pill)',
            background: markColor('accent'),
          }}
        />
        {code && (
          <Mono style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)', flexShrink: 0 }}>
            {code}
          </Mono>
        )}
        <span
          style={{
            fontSize: 'var(--hf-text-sm)',
            color: 'var(--hf-ink)',
            maxWidth: 160,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </span>
        {/* This timer's own time, not the day's: the day pill beside it has that. */}
        <Mono
          style={{
            fontSize: 'var(--hf-text-sm)',
            color: TONE.accent.fg,
            fontWeight: 'var(--hf-weight-medium)',
            margin: '0 var(--hf-space-2)',
            minWidth: '7ch',
          }}
        >
          {fmtDuration(elapsed, 'clock')}
        </Mono>
        <Button
          variant="ghost"
          icon="folder"
          ariaLabel="Switch project"
          title="Switch to another project"
          ariaHasPopup="dialog"
          disabled={busy}
          focusableWhenDisabled
          onClick={() => setPicking(true)}
          style={{ ...ROUND, color: TONE.accent.fg, background: TONE.accent.bg }}
        />
        <Button
          variant="ghost"
          icon="stop"
          ariaLabel="Stop timer"
          title="Stop the timer — you stay checked in"
          ariaHasPopup="dialog"
          disabled={busy}
          focusableWhenDisabled
          onClick={() => setStopping(true)}
          style={{ ...ROUND, color: TONE.neutral.fg, background: TONE.neutral.bg }}
        />
      </div>
      {picker}
      {stopping && (
        <StopTimerModal
          seconds={elapsed}
          label={code ? `${code} · ${name}` : name}
          busy={busy}
          onCancel={() => setStopping(false)}
          onConfirm={(note) =>
            act(
              'stop the timer',
              () => stopTimer(note),
              () => setStopping(false),
            )
          }
        />
      )}
    </>
  );
}

// One option in the picker: a native radio in its label, so arrow keys, Space
// and the screen reader all work with no code of ours (corrections.tsx).
function Choice({
  name,
  checked,
  onChange,
  title,
  sub,
  aside,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  title: string;
  sub?: string;
  aside?: ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--hf-space-3)',
        padding: 'var(--hf-space-2) var(--hf-space-3)',
        borderRadius: 'var(--hf-radius-md)',
        border: `1px solid ${checked ? 'var(--hf-accent-line)' : 'var(--hf-rule-2)'}`,
        background: checked ? 'var(--hf-accent-wash)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>
          {title}
        </span>
        {sub && (
          <span
            style={{
              display: 'block',
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
              fontFamily: 'var(--hf-font-mono)',
            }}
          >
            {sub}
          </span>
        )}
      </span>
      {aside}
    </label>
  );
}

const LIST: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-2)',
  maxHeight: 260,
  overflowY: 'auto',
};
const HEADING: CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' };
const QUIET: CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' };
const SEARCH_FROM = 6; // a search box only once the list is longer than a glance
const matches = (q: string, ...xs: (string | null)[]) =>
  xs.some((x) => (x ?? '').toLowerCase().includes(q.trim().toLowerCase()));

// ── Picker: a project, then its work item ─────────────────────────────────────
// A Jira-linked project is timed on one of its live issues (owner, 2026-10-14);
// any other on one of its own work items (owner, 2026-10-15). A project with
// none yet gets one here: the inline Create work item, or its board.
function TimerPicker({
  uid,
  switching,
  currentProject,
  checkedOut,
  onBreak,
  onLeave,
  busy,
  onStart,
  onClose,
}: {
  uid: string;
  switching: boolean;
  currentProject: string | null;
  checkedOut: boolean;
  onBreak: boolean;
  onLeave: boolean;
  busy: boolean;
  /** Resolves to the server's refusal, shown here, or null. */
  onStart: (project: string, task: string | null) => Promise<string | null>;
  onClose: () => void;
}) {
  const ids = useId();
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<TimeableProject[] | null | 'loading'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [issues, setIssues] = useState<TimerTask[] | null | 'loading'>('loading');
  const [issueId, setIssueId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [iq, setIq] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const [making, setMaking] = useState(false);

  useEffect(() => {
    let live = true;
    void loadTimeable(uid).then((p) => live && setProjects(p));
    return () => {
      live = false;
    };
  }, [uid, attempt]);

  const project = Array.isArray(projects) ? projects.find((p) => p.id === projectId) : undefined;
  const pickedId = project?.id ?? null;
  const jira = !!project?.jira;
  useEffect(() => {
    if (!pickedId) return;
    let live = true;
    setIssues('loading');
    setIssueId(null);
    setFresh(null);
    void loadTimerTasks(pickedId, jira).then((i) => live && setIssues(i));
    return () => {
      live = false;
    };
  }, [pickedId, jira, attempt]);

  const make = async () => {
    if (!pickedId || !fresh?.trim()) return;
    setMaking(true);
    const r = await createTimerTask(pickedId, fresh);
    setMaking(false);
    if (!r.ok) return setError(r.message);
    setError(null);
    setFresh(null);
    setIssues((xs) => [r.task, ...(Array.isArray(xs) ? xs : [])]);
    setIssueId(r.task.id);
  };

  // Switching to the project already running would only restart it.
  const list = Array.isArray(projects) ? projects.filter((p) => p.id !== currentProject) : [];
  const shown = list.filter((p) => matches(q, p.name, p.code, p.client));
  const issueList = Array.isArray(issues) ? issues : [];
  const shownIssues = issueList.filter((i) => matches(iq, i.code, i.title));
  const ready = !!project && !!issueId;
  const what = jira ? 'issue' : 'work item';

  return (
    <Dialog
      open
      size="sm"
      title={switching ? 'Switch timer' : 'Start a timer'}
      onClose={onClose}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="play"
            loading={busy}
            disabled={!ready}
            focusableWhenDisabled
            onClick={() =>
              project &&
              void onStart(project.id, issueId).then((m) => {
                setError(m);
                // The project was linked to Jira, or the item left it, since
                // this list was read: read it again so the picker matches.
                if (m && /issue|work item/i.test(m)) setAttempt((n) => n + 1);
              })
            }
          >
            {switching ? 'Switch timer' : 'Start timer'}
          </Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        {error && <InlineStatus tone="error">Couldn’t start the timer. {error}</InlineStatus>}
        {onLeave ? (
          <InlineStatus tone="warning">
            You’re on approved leave today. Starting a timer checks you in. Working is fine — when
            you check out we’ll return the leave day to your balance, or half of it if you only work
            part of the day.
          </InlineStatus>
        ) : checkedOut ? (
          <div style={HEADING}>Starting a timer also checks you in for the day.</div>
        ) : onBreak ? (
          <div style={HEADING}>Starting a timer ends your break.</div>
        ) : switching ? (
          <div style={HEADING}>The running timer stops, and this one starts.</div>
        ) : null}

        {projects === null ? (
          <LoadError what="your projects" onRetry={() => setAttempt((n) => n + 1)} />
        ) : projects === 'loading' ? (
          <div role="status" style={QUIET}>
            Loading…
          </div>
        ) : list.length === 0 ? (
          <div style={HEADING}>
            {switching
              ? 'You’re not on any other open project.'
              : 'You’re not on any open project yet. A project lead adds you to its team.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
            <div id={`${ids}-p`} style={HEADING}>
              Project
            </div>
            {list.length > SEARCH_FROM && (
              <TextInput
                value={q}
                onChange={setQ}
                aria-label="Search projects"
                placeholder="Search projects…"
                leading={<Icon name="search" size={13} style={{ color: 'var(--hf-ink-3)' }} />}
              />
            )}
            <div role="radiogroup" aria-labelledby={`${ids}-p`} style={LIST}>
              {shown.map((p) => (
                <Choice
                  key={p.id}
                  name={`${ids}-project`}
                  checked={p.id === projectId}
                  onChange={() => setProjectId(p.id)}
                  title={p.name}
                  sub={[p.code, p.client].filter(Boolean).join(' · ') || undefined}
                  aside={p.jira ? <Badge tone="sky">Jira</Badge> : undefined}
                />
              ))}
              {shown.length === 0 && <div style={QUIET}>No project matches “{q}”.</div>}
            </div>
          </div>
        )}

        {project && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
            <div id={`${ids}-i`} style={HEADING}>
              {jira ? 'Jira issue' : 'Work item'}
            </div>
            {issues === null ? (
              <LoadError what={`the project’s ${what}s`} onRetry={() => setAttempt((n) => n + 1)} />
            ) : issues === 'loading' ? (
              <div role="status" style={QUIET}>
                Loading…
              </div>
            ) : issueList.length === 0 && jira ? (
              <div style={HEADING}>No issues have come across from Jira for this project yet.</div>
            ) : (
              <>
                {issueList.length === 0 && (
                  <div style={HEADING}>
                    Create a work item in this project to time it, here or on its{' '}
                    <Link to={`/project?id=${project.id}&tab=board`} onClick={onClose}>
                      board
                    </Link>
                    .
                  </div>
                )}
                {issueList.length > SEARCH_FROM && (
                  <TextInput
                    value={iq}
                    onChange={setIq}
                    aria-label={`Search ${what}s`}
                    placeholder={`Search ${what}s…`}
                    leading={<Icon name="search" size={13} style={{ color: 'var(--hf-ink-3)' }} />}
                  />
                )}
                {issueList.length > 0 && (
                  <div role="radiogroup" aria-labelledby={`${ids}-i`} style={LIST}>
                    {shownIssues.map((i) => (
                      <Choice
                        key={i.id}
                        name={`${ids}-issue`}
                        checked={i.id === issueId}
                        onChange={() => setIssueId(i.id)}
                        title={i.title}
                        sub={
                          [i.code, i.jira_status ?? i.status?.name].filter(Boolean).join(' · ') ||
                          undefined
                        }
                      />
                    ))}
                    {shownIssues.length === 0 && (
                      <div style={QUIET}>
                        No {what} matches “{iq}”.
                      </div>
                    )}
                  </div>
                )}
                {!jira &&
                  (fresh == null ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="plus"
                      onClick={() => setFresh('')}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      Create work item
                    </Button>
                  ) : (
                    <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
                      <TextInput
                        value={fresh}
                        onChange={setFresh}
                        aria-label="New work item"
                        placeholder="What are you working on?"
                        maxLength={255}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void make();
                          }
                        }}
                        style={{ flex: 1 }}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={making}
                        disabled={!fresh.trim()}
                        onClick={() => void make()}
                      >
                        Create
                      </Button>
                    </div>
                  ))}
              </>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ── Stop: an optional note on what the time went to ──────────────────────────
function StopTimerModal({
  seconds,
  label,
  busy,
  onConfirm,
  onCancel,
}: {
  seconds: number;
  label: string;
  busy: boolean;
  /** Resolves to the server's refusal, shown here, or null. */
  onConfirm: (note: string) => Promise<string | null>;
  onCancel: () => void;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      open
      size="sm"
      title="Stop the timer"
      onClose={onCancel}
      dirty={note.trim() !== ''}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="stop"
            loading={busy}
            onClick={() => void onConfirm(note).then(setError)}
          >
            Stop timer
          </Button>
        </>
      )}
    >
      {error && (
        <div style={{ marginBottom: 'var(--hf-space-3)' }}>
          <InlineStatus tone="error">Couldn’t stop the timer. {error}</InlineStatus>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 'var(--hf-space-2)',
          marginBottom: 'var(--hf-space-4)',
        }}
      >
        <Mono style={{ fontSize: 'var(--hf-text-2xl)', color: 'var(--hf-ink)' }}>
          {fmtDuration(seconds, 'clock')}
        </Mono>
        <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>on {label}</span>
      </div>
      <Field
        label="What did you work on?"
        hint={`Optional. You stay checked in. Up to ${NOTE_MAX} characters.`}
      >
        <TextArea rows={4} value={note} onChange={setNote} maxLength={NOTE_MAX} />
      </Field>
    </Dialog>
  );
}

// ── Live-timer badge (for a task card) ───────────────────────────────────────
// Shows while YOUR timer runs on this task. It reads the pill's running timer,
// so a board of cards opens no channels of its own.
export function LiveTimerBadge({ taskId }: { taskId: string }) {
  const t = useRunningTimer();
  if (t?.task_id !== taskId) return null;
  return (
    <Badge tone="accent" dot>
      Timing
    </Badge>
  );
}
